import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePlayerContext } from "../providers/PlayerProvider";
import ImmersivePlayer from "../components/ImmersivePlayer";
import { scenes } from "../data/scenes";

/**
 * ImmersivePlayerPage — 沉浸式播放器独立路由。
 * 处理 /immersive_player?track=xxx 和 /immersive_player?playlist=xxx 两种分享链接。
 */
export default function ImmersivePlayerPage({
  likedTracks,
  favoritedTracks,
  onToggleLike,
  onToggleFavorite,
  playlists,
  lang = "zh",
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { playTrack, setPlaylist, setPlaylistSource, setPlayerMode, playerMode } =
    usePlayerContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  // ── 进入时设为 immersive 模式 ──
  useEffect(() => {
    setPlayerMode("immersive");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 关闭时（用户点了返回/关闭按钮）→ 回到首页 ──
  useEffect(() => {
    if (playerMode === "bar" || playerMode === "fullscreen") {
      navigate("/");
    }
  }, [playerMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 解析参数，fetch 数据，开始播放 ──
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const trackId = searchParams.get("track");
    const playlistId = searchParams.get("playlist");

    if (trackId) {
      // 单曲分享
      fetch(`/api/tracks/${trackId}`)
        .then((r) => {
          if (!r.ok) throw new Error("Track not found");
          return r.json();
        })
        .then((track) => {
          const scene = scenes.find((s) => s.category === track.category) || null;
          playTrack(track, scene);
          setLoading(false);
        })
        .catch((e) => {
          console.error("ImmersivePlayerPage: fetch track failed", e);
          setError(e.message);
          setLoading(false);
        });
    } else if (playlistId) {
      // 歌单分享 — 需要带 auth token
      const token = localStorage.getItem("fm_token");
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      fetch(`/api/playlists/${playlistId}`, { headers })
        .then((r) => {
          if (!r.ok) throw new Error("Playlist not found");
          return r.json();
        })
        .then((data) => {
          const items = data.tracks || [];
          const tracks = items
            .map((item) => item.track)
            .filter(Boolean);

          if (tracks.length === 0) throw new Error("Empty playlist");

          setPlaylist(tracks);
          setPlaylistSource("playlist");

          const first = tracks[0];
          const scene = scenes.find((s) => s.category === first.category) || null;
          playTrack(first, scene);
          setLoading(false);
        })
        .catch((e) => {
          console.error("ImmersivePlayerPage: fetch playlist failed", e);
          setError(e.message);
          setLoading(false);
        });
    } else {
      // 无参数 → 回到首页
      navigate("/");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Loading ──
  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center text-white gap-6">
        <div className="text-6xl">😕</div>
        <p className="text-gray-400 text-lg">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition"
        >
          Back to Home
        </button>
      </div>
    );
  }

  // ── 播放器 ──
  return (
    <div className="fixed inset-0 z-[100]">
      <ImmersivePlayer
        likedTracks={likedTracks}
        favoritedTracks={favoritedTracks}
        onToggleLike={onToggleLike}
        onToggleFavorite={onToggleFavorite}
        playlists={playlists}
        lang={lang}
      />
    </div>
  );
}
